import { Button } from "design-system/atoms/Button"
import { Calendar } from "design-system/atoms/Calendar"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { DateRange } from "design-system/molecules/DatePicker"
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "design-system/molecules/Command"
import { cn } from "design-system/utils"
import { addDays, differenceInCalendarDays, subDays } from "date-fns"
import { DAYS_TO_MILLIS, endOfDay, formatDate, isSameDay, startOfDay } from "lopermut"
import { ArrowRight, Calendar as CalendarIcon, Check, ChevronDown, ChevronLeft } from "lucide-react"
import React from "react"
import { useTranslation } from "react-i18next"
import {
  getTimeRange,
  MAX_TIME_FRAME_SPAN,
  TimeFrame,
  TimeFrameEnum,
  TimeFramePreset,
} from "schema"

const DEFAULT_PRESETS = [
  TimeFrameEnum.Last2Weeks,
  TimeFrameEnum.LastMonth,
  TimeFrameEnum.Last3Months,
  TimeFrameEnum.Last6Months,
  TimeFrameEnum.Last12Months,
] satisfies TimeFramePreset[]

type Props = {
  value: TimeFrame
  presets?: readonly TimeFramePreset[]
  onChange: (value: TimeFrame) => void
}

export function TimeFrameSelector({ value, presets = DEFAULT_PRESETS, onChange }: Props) {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = React.useState(false)
  const [isPickingRange, setIsPickingRange] = React.useState(false)
  const [hasOpenedRangePicker, setHasOpenedRangePicker] = React.useState(false)
  const [draftRange, setDraftRange] = React.useState<DateRange | undefined>()

  const isCustom = typeof value !== "string"

  const today = new Date()
  const maxSpanInDays = getMaxSpanInDays(presets)
  const anchor = isCompleteRange(draftRange) ? undefined : draftRange?.from
  const disabledDays = [
    { after: today },
    ...(anchor
      ? [
          { before: subDays(anchor, maxSpanInDays - 1) },
          { after: addDays(anchor, maxSpanInDays - 1) },
        ]
      : []),
  ]

  function close() {
    setIsOpen(false)
    setIsPickingRange(false)
    setHasOpenedRangePicker(false)
  }

  function startPickingRange() {
    setDraftRange(isCustom ? { from: new Date(value.from), to: new Date(value.to) } : undefined)
    setIsPickingRange(true)
    setHasOpenedRangePicker(true)
  }

  function selectDraftRange(range: DateRange | undefined, selectedDay: Date) {
    if (isCompleteRange(draftRange)) {
      setDraftRange({ from: selectedDay, to: selectedDay })
      return
    }

    if (!range?.from || !range.to) {
      setDraftRange(range)
      return
    }

    const spanInDays = differenceInCalendarDays(range.to, range.from) + 1

    setDraftRange(spanInDays <= maxSpanInDays ? range : { from: selectedDay, to: undefined })
  }

  function applyDraftRange() {
    if (!draftRange?.from || !draftRange.to) return

    onChange({ from: startOfDay(draftRange.from).getTime(), to: endOfDay(draftRange.to).getTime() })
    close()
  }

  const label = isCustom
    ? `${formatDate(new Date(value.from))} - ${formatDate(new Date(value.to))}`
    : t(`shared.timeRange.frame.${value}`)

  return (
    <Popover open={isOpen} onOpenChange={(open) => (open ? setIsOpen(true) : close())}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-10 gap-2 px-3">
          <CalendarIcon className="h-4 w-4" />
          {label}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="end">
        {isPickingRange ? (
          <div className="flex animate-in flex-col duration-150 fade-in-0 slide-in-from-right-4">
            <Calendar
              mode="range"
              selected={draftRange}
              onSelect={selectDraftRange}
              defaultMonth={draftRange?.from}
              disabled={disabledDays}
              numberOfMonths={2}
            />
            <div className="flex items-center justify-between border-t border-border p-3">
              <Button
                variant="secondary"
                size="sm"
                className="gap-1.5"
                onClick={() => setIsPickingRange(false)}
              >
                <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
                {t("shared.back")}
              </Button>
              <Button size="sm" disabled={!draftRange?.to} onClick={applyDraftRange}>
                {t("shared.timeRange.apply")}
              </Button>
            </div>
          </div>
        ) : (
          <Command
            className={cn(
              "w-56",
              hasOpenedRangePicker && "animate-in duration-150 fade-in-0 slide-in-from-left-4",
            )}
          >
            <CommandList>
              <CommandGroup
                heading={t("shared.timeRange.label")}
                className="[&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:text-foreground"
              >
                {presets.map((preset) => (
                  <CommandItem
                    key={preset}
                    value={preset}
                    onSelect={() => {
                      onChange(preset)
                      close()
                    }}
                  >
                    <span className="grow">{t(`shared.timeRange.frame.${preset}`)}</span>
                    <Check className={cn("h-3.5 w-3.5", value !== preset && "opacity-0")} />
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem value="absolute" onSelect={startPickingRange}>
                  <span className="grow">{t("shared.timeRange.absolute")}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  )
}

const isCompleteRange = (range: DateRange | undefined) => {
  return Boolean(range?.from && range.to && !isSameDay(range.from, range.to))
}

const getMaxSpanInDays = (presets: readonly TimeFramePreset[]) => {
  const maxInDays = Math.floor(MAX_TIME_FRAME_SPAN / DAYS_TO_MILLIS)
  const spans = presets.map((preset) => {
    const { from, to } = getTimeRange(preset)

    return differenceInCalendarDays(to, from) + 1
  })

  if (spans.length === 0) return maxInDays

  return Math.min(maxInDays, Math.max(...spans))
}
